import { useEffect, useMemo, useState } from "react";
import { optimizeUrl } from "@/lib/img";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import SectionTabs from "@/components/SectionTabs";
import { Trophy, Sparkles, Clock, ArrowUpRight, CalendarDays, Users, Heart, Crown, Hourglass, Medal, Calendar } from "lucide-react";

const monthLabel = (key) => {
  if (!key) return "Sans date";
  const [y, m] = key.split("-").map(Number);
  const s = new Date(y, (m || 1) - 1, 1).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long" }) : "");

export default function Concours() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [user, loading, navigate]);

  useEffect(() => {
    api.get("/concours")
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]))
      .finally(() => setFetching(false));
  }, []);

  const current = items.find((c) => c.is_current) || null;
  const grouped = useMemo(() => {
    const rest = items.filter((c) => c.id !== current?.id);
    const map = new Map();
    rest.forEach((c) => { const k = c.month_key || ""; if (!map.has(k)) map.set(k, []); map.get(k).push(c); });
    return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [items, current]);

  if (!user) return null;
  const isTalent = user.role === "talent";

  return (
    <AppShell>
      <div data-testid="concours-page">
        {isTalent ? (
          <SectionTabs
            eyebrow="L'agenda"
            title="Concours"
            subtitle="Chaque mois, un thème, un podium et des récompenses : visibilité, Premium offert, shootings."
            tabs={[
              { id: "events", to: "/events", label: "Événements", icon: CalendarDays },
              { id: "concours", to: "/talent/concours", label: "Concours", icon: Trophy },
              { id: "my-entries", to: "/talent/my-contest-entries", label: "Mes participations", icon: Heart },
              { id: "castings", to: "/talent/castings", label: "Castings", icon: Users },
            ]}
          />
        ) : (
          <div className="mb-8">
            <p className="caption-label text-[#C68E59]">Concours</p>
            <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-1">Les concours Helencia</h1>
            <p className="text-sm text-[#8B8680] mt-2 max-w-2xl">Repérez les talents qui montent : chaque mois la communauté vote pour son podium.</p>
          </div>
        )}

        {fetching ? (
          <div className="flex items-center justify-center py-24"><div className="w-10 h-10 border-2 border-[#C68E59] border-t-transparent rounded-full animate-spin" /></div>
        ) : (
          <>
            {current ? <CurrentConcours c={current} isTalent={isTalent} /> : <NoCurrent />}

            {grouped.length > 0 && (
              <div className="mt-14" data-testid="concours-archive">
                <div className="flex items-baseline justify-between mb-6">
                  <div>
                    <p className="caption-label text-[#A26F3E]">Historique</p>
                    <h2 className="font-serif text-2xl sm:text-3xl text-[#1A1A1A] mt-1">Les éditions précédentes</h2>
                  </div>
                  <span className="text-xs text-[#8B8680]">{items.length - (current ? 1 : 0)} concours</span>
                </div>
                {grouped.map(([k, list]) => (
                  <section key={k} className="mb-10" data-testid={`concours-month-${k}`}>
                    <div className="flex items-center gap-4 mb-4">
                      <h3 className="font-serif text-xl text-[#1A1A1A]">{monthLabel(k)}</h3>
                      <div className="flex-1 h-px bg-[#EADDC9]" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {list.map((c) => <ConcoursCard key={c.id} c={c} />)}
                    </div>
                  </section>
                ))}
              </div>
            )}

            {items.length === 0 && (
              <div className="bg-white rounded-3xl border border-[#EADDC9] p-12 text-center text-[#8B8680]" data-testid="concours-empty">Aucun concours disponible pour l'instant.</div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

function CurrentConcours({ c, isTalent }) {
  const top = c.top_entries || [];
  return (
    <section className="relative rounded-[2rem] overflow-hidden bg-[#1A1A1A] text-white shadow-[0_30px_80px_-40px_rgba(0,0,0,0.6)]" data-testid="concours-current">
      <div className="grid lg:grid-cols-5">
        <div className="lg:col-span-2 relative aspect-[4/3] lg:aspect-auto lg:min-h-[480px] bg-[#2a2a2a]">
          {c.image_url && <img src={optimizeUrl(c.image_url, 1024)} alt={c.title} className="absolute inset-0 w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A] via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:to-[#1A1A1A]" />
          <div className="absolute top-5 left-5 flex flex-wrap gap-2">
            <span className="px-3 py-1.5 rounded-full bg-[#C68E59] text-[10px] uppercase tracking-[0.16em] font-semibold">Concours du mois</span>
            <span className="px-3 py-1.5 rounded-full bg-white/90 text-[#1A1A1A] text-[10px] uppercase tracking-[0.16em] font-semibold">{monthLabel(c.month_key)}</span>
          </div>
        </div>
        <div className="lg:col-span-3 p-8 sm:p-10 lg:p-12 relative overflow-hidden">
          <div className="absolute -top-24 -right-20 w-72 h-72 rounded-full bg-[#C68E59]/25 blur-[100px] pointer-events-none" />
          <div className="relative">
            <p className="caption-label text-[#E6C29A] inline-flex items-center gap-2"><Trophy className="w-3.5 h-3.5" /> {c.theme}</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05]">{c.title}</h2>
            <p className="text-white/70 text-sm mt-4 leading-relaxed line-clamp-3 max-w-2xl">{c.description}</p>

            <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="concours-current-stats">
              <Stat icon={Hourglass} label="Temps restant" value={c.days_left != null ? `${c.days_left} j` : "—"} testid="stat-days-left" />
              <Stat icon={Users} label="Participants" value={c.participants_count ?? 0} testid="stat-participants" />
              <Stat icon={Heart} label="Votes" value={c.votes_total ?? 0} testid="stat-votes" />
              <Stat icon={Sparkles} label="Récompense" value={c.prize || "—"} small testid="stat-prize" />
            </div>

            {top.length > 0 && (
              <div className="mt-7" data-testid="concours-current-top">
                <p className="text-[10px] uppercase tracking-[0.18em] text-white/50 mb-3">Classement provisoire</p>
                <div className="flex flex-wrap gap-2">
                  {top.map((t) => (
                    <div key={t.id} className="inline-flex items-center gap-2.5 rounded-full bg-white/8 border border-white/10 pl-1.5 pr-4 py-1.5">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${t.rank === 1 ? "bg-[#E6C29A] text-[#1A1A1A]" : "bg-white/15"}`}>{t.rank}</span>
                      {t.user?.picture ? <img src={t.user.picture} alt="" className="w-6 h-6 rounded-full object-cover" /> : null}
                      <span className="text-xs font-medium">{t.user?.name}</span>
                      <span className="text-[11px] text-white/60 inline-flex items-center gap-1"><Heart className="w-3 h-3 text-[#C68E59] fill-[#C68E59]" />{t.votes_count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to={`/concours/${c.id}`} className="inline-flex items-center gap-2 rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white text-sm font-semibold px-7 h-12 transition" data-testid={`participate-${c.id}`}>
                {c.has_participated ? "Voir ma participation" : isTalent ? "Participer maintenant" : "Voir & voter"} <ArrowUpRight className="w-4 h-4" />
              </Link>
              <span className="text-xs text-white/60 inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Clôture le {fmt(c.end_date)}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ icon: Icon, label, value, small, testid }) {
  return (
    <div className="rounded-2xl bg-white/6 border border-white/10 p-3.5" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/55"><Icon className="w-3 h-3 text-[#E6C29A]" /> {label}</div>
      <div className={`${small ? "text-sm font-medium truncate" : "font-serif text-2xl"} mt-1.5 leading-none`}>{value}</div>
    </div>
  );
}

function NoCurrent() {
  return (
    <section className="rounded-[2rem] bg-gradient-to-br from-[#FAF3EA] via-white to-[#F5E6D0] border border-[#E6C29A] p-10 sm:p-14 text-center" data-testid="concours-no-current">
      <div className="w-14 h-14 rounded-full bg-[#1A1A1A] text-[#E6C29A] flex items-center justify-center mx-auto"><Calendar className="w-6 h-6" /></div>
      <p className="caption-label text-[#A26F3E] mt-5">Prochain concours</p>
      <h2 className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] mt-2">Le thème du mois arrive bientôt</h2>
      <p className="text-sm text-[#8B8680] mt-3 max-w-md mx-auto">Préparez vos meilleures photos : un nouveau concours ouvre chaque mois avec son podium et ses récompenses.</p>
    </section>
  );
}

function ConcoursCard({ c }) {
  const winner = !c.is_open ? (c.top_entries || [])[0] : null;
  return (
    <div className="bg-white rounded-3xl border border-[#EADDC9] overflow-hidden flex flex-col group" data-testid={`concours-${c.id}`}>
      <div className="relative h-52 overflow-hidden bg-[#FAF3EA]">
        {c.image_url && <img src={optimizeUrl(c.image_url, 800)} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />}
        <span className={`absolute top-3 left-3 text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${c.is_open ? "bg-[#E7F5EC] text-[#2E7D32]" : "bg-white/90 text-[#1A1A1A]"}`} data-testid={`status-${c.id}`}>
          {c.is_open ? "Ouvert" : "Terminé"}
        </span>
        {winner && (
          <div className="absolute bottom-3 left-3 inline-flex items-center gap-2 rounded-full bg-[#1A1A1A]/85 backdrop-blur text-white pl-1.5 pr-3 py-1.5" data-testid={`winner-${c.id}`}>
            {winner.user?.picture ? <img src={winner.user.picture} alt="" className="w-6 h-6 rounded-full object-cover" /> : <Crown className="w-5 h-5 text-[#E6C29A]" />}
            <span className="text-xs"><span className="text-[#E6C29A]">Lauréat·e</span> · {winner.user?.name}</span>
          </div>
        )}
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <div className="flex items-center gap-2 text-[#C68E59]"><Medal className="w-3.5 h-3.5" /><span className="caption-label">{c.theme}</span></div>
        <h3 className="font-serif text-2xl text-[#1A1A1A] mt-2 leading-tight">{c.title}</h3>
        <p className="text-sm text-[#666] leading-relaxed mt-2 line-clamp-2 flex-1">{c.description}</p>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-[#8B8680]">
          {c.prize && <span className="inline-flex items-center gap-1.5"><Sparkles className="w-3 h-3 text-[#C68E59]" />{c.prize}</span>}
          <span className="inline-flex items-center gap-1.5" data-testid={`participants-${c.id}`}><Users className="w-3 h-3" />{c.participants_count ?? 0} participant{(c.participants_count ?? 0) > 1 ? "s" : ""}</span>
          <span className="inline-flex items-center gap-1.5"><Heart className="w-3 h-3" />{c.votes_total ?? 0} vote{(c.votes_total ?? 0) > 1 ? "s" : ""}</span>
          <span className="inline-flex items-center gap-1.5"><Clock className="w-3 h-3" />{fmt(c.start_date)} → {fmt(c.end_date)}</span>
        </div>
        <Link to={`/concours/${c.id}`} className="mt-5 inline-flex items-center gap-2 text-xs font-semibold text-[#1A1A1A] hover:text-[#C68E59] transition" data-testid={`participate-${c.id}`}>
          {c.is_open ? "Voir & participer" : "Voir le podium"} <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </div>
  );
}
